import { atom, AtomEffect } from "recoil";
import { recoilPersist } from "recoil-persist";
import { GIVE_BASE_CODE } from "src/config";
import {
  EvaluatePremium,
  FormikInsurance,
  FormikContractorLegal,
  FormikContractorNatural,
  FormikInsured,
  Optional,
  Dossier,
  ContractorType,
  Policy,
  PeriodType,
  Questionnaire,
  DossierConfiguration,
  Viario,
  Nationality,
  YesNoAnswersNew,
  Gender,
} from "src/types";
import { FormikGroup } from "src/types/networking/terminals/group.types";
import { formatDate } from "src/utils";

/**
 * Utility function to create a persistAtom configuration.
 *
 * This function wraps the `recoilPersist` initializer, allowing dynamic configuration
 * of the key used in localStorage for persisting Recoil atoms.
 *
 * It returns an AtomEffect that can be added to the `effects_UNSTABLE` array of an atom,
 * enabling automatic persistence of that atom's state in localStorage.
 *
 * @param key - The unique storage key used to persist the atom in localStorage.
 * @returns An AtomEffect to apply to a Recoil atom for state persistence.
 */
const createPersistAtom = (
  key: string,
  storage: Storage = localStorage
): AtomEffect<any> => {
  const { persistAtom } = recoilPersist({
    key: key, // this key is using to store data in local storage
    storage: storage, // configure which storage will be used to store the data
    converter: JSON, // configure how values will be serialized/deserialized in storage
  });
  return persistAtom;
};

//Dossier
const ATOM_KEY_DOSSIER = "dossier";
export const atomDossier = atom<Optional<Dossier>>({
  key: ATOM_KEY_DOSSIER,
  default: undefined,
  effects_UNSTABLE: [createPersistAtom(ATOM_KEY_DOSSIER)],
});

// Dossier Configuration
const ATOM_KEY_DOSSIER_CONFIG = "dossierConfig";
export const atomDossierConfig = atom<Optional<DossierConfiguration>>({
  key: ATOM_KEY_DOSSIER_CONFIG,
  default: undefined,
  effects_UNSTABLE: [createPersistAtom(ATOM_KEY_DOSSIER_CONFIG)],
});

//Policy
const ATOM_KEY_POLICY = "policy";
export const atomPolicy = atom<Optional<Policy>>({
  key: ATOM_KEY_POLICY,
  default: undefined,
  effects_UNSTABLE: [createPersistAtom(ATOM_KEY_POLICY)],
});

//Questionnaire
const ATOM_KEY_QUESTIONNAIRE = "questionnaire";
export const atomQuestionnaire = atom<Optional<Questionnaire>>({
  key: ATOM_KEY_QUESTIONNAIRE,
  default: undefined,
});

export const defaultSelectInputOptionValue = {
  id: "",
  value: "",
};

// Entry
export const atomShowQuestionnaireFailure = atom<Optional<boolean>>({
  key: "showQuestionnaireFailure",
  default: false,
});
export const defaultValueInsured: FormikInsured = {
  insuredName: "",
  insuredSurname: "",
  insuredBirth: "",
  insuredBirthNation: "",
  insuredBirthPlace: "",
  insuredFc: "",
  insuredGender: Gender.M,
  insuredFcQuestion: YesNoAnswersNew.YES,
  insuredNationality: Nationality.IT,
};
export const defaultValueContractorNatural: FormikContractorNatural = {
  contractorName: "",
  contractorSurname: "",
  contractorEmail: "",
  contractorEmailConfirm: "",
  contractorPhone: {
    country: {
      name: "",
      dialCode: "",
      countryCode: "",
      format: "",
    },
    formattedPhone: "",
    value: "",
  },
  contractorNationality: Nationality.IT,
  contractorCap: {...defaultSelectInputOptionValue},
  contractorFcQuestion: YesNoAnswersNew.YES,
  contractorFc: "",
  contractorResidenceProvince: {...defaultSelectInputOptionValue},
  contractorResidenceMunicipality: {...defaultSelectInputOptionValue},
  contractorRedisenceAddress: "",
  contractorBirth: "",
  contractorBirthNation: "",
  contractorBirthPlace: "",
  contractorBirthProvince: "",
  contractorGender: Gender.M,
  contractorIsInsuredQuestion: YesNoAnswersNew.YES,
};
export const defaultValueContractorLegal: FormikContractorLegal = {
  companyName: "",
  companyFC: "",
  companyVAT: "",
  companyLegalProvince: {...defaultSelectInputOptionValue},
  companyLegalCity: {...defaultSelectInputOptionValue},
  companyLegalAddress: "",
  companyLegalCap: {...defaultSelectInputOptionValue},
  companyPhone: {
    country: {
      name: "",
      dialCode: "",
      countryCode: "",
      format: "",
    },
    formattedPhone: "",
    value: "",
  },
  companyFCNotMatchVAT: false,
  companyType: "",
  companyEmail: "",
  companyEmailConfirm: "",
};
const ATOM_KEY_FORMIK_INSURANCE = "FormikInsurance";
export const defaultValueInsurance: FormikInsurance = {
  coverageTypeRadios: GIVE_BASE_CODE,
  insuredNumber: "1",
  insuredPeriodType: PeriodType.DAILY,
  insuredDaysNumber: "1",
  coverageStartDate: formatDate(new Date()),
  coverageEndDate: formatDate(new Date()),
  contractorType: ContractorType.LEGAL,
  consentPrivacy: false,
  consentMarketing: false,
  consentProfiling: false,
  consentCommunication: false,
  contractor: defaultValueContractorLegal,
  insuredList: [defaultValueInsured],
  consentTd: false,
  informativeVision: false,
  coherenceQuestions: [],
};
export const atomFormikInsurance = atom<FormikInsurance>({
  key: ATOM_KEY_FORMIK_INSURANCE,
  default: defaultValueInsurance,
  effects_UNSTABLE: [createPersistAtom(ATOM_KEY_FORMIK_INSURANCE)],
});

// Cookie
const ATOM_KEY_RELOAD_COOKIE = "reloadCookie";
export const atomReloadCookie = atom<boolean>({
  key: ATOM_KEY_RELOAD_COOKIE,
  default: false,
  effects_UNSTABLE: [createPersistAtom(ATOM_KEY_RELOAD_COOKIE)],
});

// Current Step
const ATOM_KEY_CURRENT_STEP = "currentStep";
export const atomCurrentStep = atom<number>({
  key: ATOM_KEY_CURRENT_STEP,
  default: 0,
  effects_UNSTABLE: [createPersistAtom(ATOM_KEY_CURRENT_STEP)],
});

const ATOM_KEY_FORMIK_GROUP = "atomFormikGroup";
export const atomFormikGroup = atom<FormikGroup>({
  key: ATOM_KEY_FORMIK_GROUP,
  default: {
    group: [],
    fileName: "",
    filesStatus: undefined,
  },
  effects_UNSTABLE: [createPersistAtom(ATOM_KEY_FORMIK_GROUP, sessionStorage)],
});

// Group
const ATOM_KEY_IS_GROUP = "atomIsGroup";
export const atomIsGroup = atom<boolean>({
  key: ATOM_KEY_IS_GROUP,
  default: false,
  effects_UNSTABLE: [createPersistAtom(ATOM_KEY_IS_GROUP, sessionStorage)],
});

export const atomEvaluatePremium = atom<Optional<EvaluatePremium>>({
  key: "evaluatePremium",
  default: undefined,
});

//Viario Provinces
const ATOM_KEY_PROVINCES = "provinces";
export const atomProvinces = atom<Viario[]>({
  key: ATOM_KEY_PROVINCES,
  default: [],
});

// App
export const atomFromApp = atom<boolean>({
  key: "fromApp",
  default: false,
});
